import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Email,
  WhatsApp,
  CheckCircle,
  Error,
  Refresh,
  Send,
  Link as LinkIcon,
  Schedule,
} from '@mui/icons-material';
import { useBookingConfirmationTracker, useBookingConfirmationActions } from '@/hooks/api/useBookingConfirmations';

interface BookingConfirmationStatusProps {
  bookingId: string;
  listingId?: string;
  compact?: boolean;
}

export const BookingConfirmationStatus: React.FC<BookingConfirmationStatusProps> = ({
  bookingId,
  listingId,
  compact = false,
}) => {
  const { status, loading, error, refreshStatus } = useBookingConfirmationTracker(bookingId);
  const { retryConfirmation, sendReminder, generateCheckInForm, loading: actionLoading } = useBookingConfirmationActions(bookingId);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleRetryConfirmation = async () => {
    setActionError(null);
    try {
      await retryConfirmation(listingId);
      await refreshStatus();
    } catch (err) {
      setActionError((err as Error)?.message || 'Failed to retry confirmation');
    }
  };

  const handleSendReminder = async () => {
    setActionError(null);
    try {
      await sendReminder(customMessage || undefined);
      setReminderDialogOpen(false);
      setCustomMessage('');
      await refreshStatus();
    } catch (err) {
      setActionError((err as Error)?.message || 'Failed to send reminder');
    }
  };

  // Check-in forms are now automatically generated when bookings are created

  const getStatusIcon = (sent: boolean, failed?: boolean) => {
    if (failed) return <Error color="error" fontSize="small" />;
    if (sent) return <CheckCircle color="success" fontSize="small" />;
    return <Schedule color="warning" fontSize="small" />;
  };

  const getStatusText = (sent: boolean, failed?: boolean) => {
    if (failed) return 'Failed';
    if (sent) return 'Sent';
    return 'Pending';
  };

  const getStatusColor = (sent: boolean, failed?: boolean): 'success' | 'error' | 'warning' => {
    if (failed) return 'error';
    if (sent) return 'success';
    return 'warning';
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  if (loading && !status) {
    return compact ? (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={12} />
        <Typography variant="body2" fontSize="0.75rem">Loading...</Typography>
      </Box>
    ) : (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        <Typography variant="body2">Loading confirmation status...</Typography>
      </Box>
    );
  }

  if (error) {
    return compact ? (
      <Typography variant="body2" color="error.main" fontSize="0.75rem">
        Error loading confirmations
      </Typography>
    ) : (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!status) {
    return (
      <Typography variant="body2" color="text.secondary">
        No confirmation status available
      </Typography>
    );
  }

  if (compact) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Chip
          icon={getStatusIcon(status.confirmationsSent?.email || false)}
          label="Email"
          size="small"
          color={getStatusColor(status.confirmationsSent?.email || false)}
          variant="outlined"
        />
        <Chip
          icon={getStatusIcon(status.confirmationsSent?.whatsapp || false)}
          label="WhatsApp"
          size="small"
          color={getStatusColor(status.confirmationsSent?.whatsapp || false)}
          variant="outlined"
        />
        {(status.checkInFormUrl || status.checkInFormGenerated) && (
          <Chip
            icon={<LinkIcon />}
            label="Check-in Form"
            size="small"
            color="success"
            variant="outlined"
          />
        )}
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="between" alignItems="center" mb={2}>
          <Typography variant="h6">Booking Confirmations</Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh status">
              <IconButton size="small" onClick={refreshStatus} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              startIcon={<Send />}
              onClick={() => setReminderDialogOpen(true)}
              disabled={actionLoading}
            >
              Send Reminder
            </Button>
          </Box>
        </Box>

        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        <Box display="flex" gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Email fontSize="small" />
            <Typography variant="body2">Email:</Typography>
            <Chip
              icon={getStatusIcon(status.confirmationsSent?.email || false)}
              label={getStatusText(status.confirmationsSent?.email || false)}
              size="small"
              color={getStatusColor(status.confirmationsSent?.email || false)}
            />
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <WhatsApp fontSize="small" />
            <Typography variant="body2">WhatsApp:</Typography>
            <Chip
              icon={getStatusIcon(status.confirmationsSent?.whatsapp || false)}
              label={getStatusText(status.confirmationsSent?.whatsapp || false)}
              size="small"
              color={getStatusColor(status.confirmationsSent?.whatsapp || false)}
            />
          </Box>
        </Box>

        {/* Resend Confirmations Button */}
        <Box display="flex" gap={1} mb={2}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Send />}
            onClick={handleRetryConfirmation}
            disabled={actionLoading}
          >
            Resend Confirmations
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Send />}
            onClick={() => setReminderDialogOpen(true)}
            disabled={actionLoading}
          >
            Send Custom Reminder
          </Button>
        </Box>

        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <LinkIcon fontSize="small" />
            <Typography variant="body2">Check-in Form:</Typography>
            {status.checkInFormUrl ? (
              <Chip
                icon={<CheckCircle />}
                label="Generated"
                size="small"
                color="success"
              />
            ) : status.checkInFormGenerated ? (
              <Chip
                icon={<CheckCircle />}
                label="Auto-Generated"
                size="small"
                color="success"
              />
            ) : (
              <Chip
                icon={<Schedule />}
                label="Generating..."
                size="small"
                color="warning"
              />
            )}
          </Box>
          
          {status.checkInFormExpiration && (
            <Typography variant="caption" color="text.secondary">
              Expires: {formatTimestamp(status.checkInFormExpiration)}
            </Typography>
          )}
        </Box>

        {status.retryCount > 0 && (
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Refresh fontSize="small" color="warning" />
            <Typography variant="body2" color="warning.main">
              {status.retryCount} retry attempt{status.retryCount !== 1 ? 's' : ''}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={handleRetryConfirmation}
              disabled={actionLoading}
            >
              Retry Now
            </Button>
          </Box>
        )}

        {status.lastAttempt && (
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Last attempt: {formatTimestamp(status.lastAttempt)}
          </Typography>
        )}


      </CardContent>

      {/* Send Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onClose={() => setReminderDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Send Booking Reminder</DialogTitle>
        <DialogContent>
          <TextField
            label="Custom Message (Optional)"
            multiline
            rows={4}
            fullWidth
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Add a personal message to the reminder..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendReminder}
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Send />}
          >
            Send Reminder
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default BookingConfirmationStatus;